/**
 * The post chain, on the CPU, for one still 64 px image.
 *
 * The portrait has to belong to the same world as the arena, and in this game the world
 * is not the geometry — it is the tube the geometry is displayed through. A face drawn
 * from `palette.ts` and blitted straight into the DOM would sit next to the arena
 * looking like a sticker.
 *
 * The shader cannot be reused literally: `CRT_COMPOSITE_FRAG` runs against four render
 * targets inside a `WebGLRenderer`, and a portrait is composed once, off the frame, into
 * an `ImageData`. So this is a MIRROR, and the mirror is defended two ways:
 *
 *  1. Every operation below is transcribed line-for-line from `src/render/shaders/crt.ts`,
 *     `bloom.ts`, `halation.ts` and `common.ts`, in the same order, including the two
 *     order-of-operations facts those files exist to protect: every light-forming term
 *     happens in linear HDR, and the tonemap comes after all of them.
 *  2. `tests/captain-portrait.test.ts` reads the shader sources and the pass defaults as
 *     TEXT and asserts the numbers here still match. If somebody retunes the tube, the
 *     gate fails and this file gets retuned with it, instead of the portrait quietly
 *     drifting out of the world.
 *
 * Three things the arena has and a still portrait does not, each dropped for a reason,
 * not for convenience:
 *
 *  - **Persistence.** `tube()` is `max(current, trail * uPersistMix)`. A still image is
 *    its own previous frame, so the trail term is the identity and drops out.
 *  - **Barrel warp.** `CrtComposite.setGeometry` zeroes `uCurvature` and `uOverscan`
 *    below a 900 px output height. A 192 px portrait is below it. `warp()` is therefore
 *    the identity here, exactly as it is on a phone.
 *  - **Time.** `uTime` drives the grain only. A portrait is composed once, so time is 0
 *    and the grain is frozen — which is what you want; a face that hisses is a face
 *    nobody looks at.
 *
 * One deliberate deviation, called out rather than buried: `CrtComposite.applyScanline`
 * killed the grille below a 900 px output height because at arena scale a scanline period
 * lands on top of a source pixel. The portrait renders at an explicit 3× integer scale,
 * which is the condition that rule protects, and `uMaskFade`'s own smoothstep over
 * `scale` already handles it. So the height gate is replaced by the scale gate and the
 * AMD-6 D12 readability cap (0.06) is not applied: that cap is a telegraph-contrast
 * argument about enemies, and a portrait telegraphs nothing. `scanline` is an option;
 * pass `SCANLINE_READABILITY_CAP` to get the arena's clamp back.
 */

import { GRAIN_MAX, SCANLINE_DEFAULT, SCANLINE_MAX, VIGNETTE_DEFAULT } from '../render/passes/CrtComposite';

// ---------------------------------------------------------------------------
// GLSL_COLOR, transcribed (src/render/shaders/common.ts)
// ---------------------------------------------------------------------------

export function luma(r: number, g: number, b: number): number {
  return r * 0.2126 + g * 0.7152 + b * 0.0722;
}

function acesScalar(x: number): number {
  const a = 2.51;
  const b = 0.03;
  const c = 2.43;
  const d = 0.59;
  const e = 0.14;
  const v = (x * (a * x + b)) / (x * (c * x + d) + e);
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

const acesOut = new Float32Array(3);

/** AMD-3's hue-preserving ACES. The peak channel takes the curve; chromaticity carries. */
function acesFilm(r: number, g: number, b: number): Float32Array {
  const peak = Math.max(r, Math.max(g, b));
  if (peak < 1e-5) {
    acesOut[0] = 0;
    acesOut[1] = 0;
    acesOut[2] = 0;
    return acesOut;
  }
  const tm = acesScalar(peak);
  let wash = (peak - 2.0) / 4.0;
  wash = wash < 0 ? 0 : wash > 1 ? 1 : wash;
  wash *= wash;
  const w = wash * 0.85;
  const rr = r / peak + (1 - r / peak) * w;
  const gg = g / peak + (1 - g / peak) * w;
  const bb = b / peak + (1 - b / peak) * w;
  acesOut[0] = Math.min(1, Math.max(0, rr * tm));
  acesOut[1] = Math.min(1, Math.max(0, gg * tm));
  acesOut[2] = Math.min(1, Math.max(0, bb * tm));
  return acesOut;
}

function linearToSrgb(c: number): number {
  return c < 0.0031308 ? c * 12.92 : 1.055 * Math.pow(Math.max(c, 1e-5), 1 / 2.4) - 0.055;
}

function fract(x: number): number {
  return x - Math.floor(x);
}

function hash12(px: number, py: number): number {
  let x = fract(px * 0.1031);
  let y = fract(py * 0.1031);
  let z = fract(px * 0.1031);
  const d = x * (y + 33.33) + y * (z + 33.33) + z * (x + 33.33);
  x += d;
  y += d;
  z += d;
  return fract((x + y) * z);
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  let t = (x - edge0) / (edge1 - edge0);
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return t * t * (3 - 2 * t);
}

// ---------------------------------------------------------------------------
// Tube parameters — every one a mirror of a shipped uniform default
// ---------------------------------------------------------------------------

/**
 * Mirrors of `CrtComposite`'s constructor defaults and `PostStack`'s applied values.
 * The pinning test reads both files as text and fails if any of these drifts.
 */
export const TUBE = {
  /** `uExposure`, §11.1 range 0.9–1.4. */
  exposure: 1.15,
  /** `uChromaticAberration`, in output pixels at the frame edge. */
  chromaticAberration: 0.6,
  /** `uMaskPitch` — aperture-grille period in OUTPUT pixels. */
  maskPitch: 3.0,
  /** `uMaskStrength`. */
  maskStrength: 0.3,
  /** `uScanlineSharpness`. */
  scanlineSharpness: 2.2,
  /** `uVignette` — AMD-6 D12's flattened falloff. */
  vignette: VIGNETTE_DEFAULT,
  /** `uNoise`. Under AMD-6 D12's `GRAIN_MAX`. */
  noise: 0.014,
  /** `PostStack.BRIGHT_PASS_FLOOR` — AMD-6 D4, only cores bloom. */
  bloomThreshold: 0.72,
  /** `0.35 * threshold` — a fixed knee turns into a hard cut when the threshold moves. */
  bloomKneeRatio: 0.35,
  /** §11.1 bloom strength, before `normalisedGain`. */
  bloomStrength: 0.85,
  /** `1.0 + BLOOM_DEFAULT.radius`. */
  bloomRadius: 1.5,
  /** `PostStack.BLOOM_WEIGHTS`, widest last. */
  bloomWeights: [1.0, 0.85, 0.68, 0.52, 0.38, 0.26] as readonly number[],
  /** `uHalationStrength` when the quality rung allows it. */
  halationStrength: 0.3,
  /** `uHalationTint` — warm, because long wavelengths survive the round trip best. */
  halationTint: [1.0, 0.72, 0.55] as readonly number[],
  /** `Halation`'s separable blur radius. */
  halationBlurRadius: 2.0,
} as const;

export { GRAIN_MAX, SCANLINE_DEFAULT, SCANLINE_MAX, VIGNETTE_DEFAULT };

export interface TubeOptions {
  /** Output pixels per source pixel. Integer; 1 in the HUD, 3 on the death card. */
  scale: number;
  /** Grille strength before the AMD-3 cap. Defaults to the AMD-3 default, 0.18. */
  scanline?: number;
  /** Grain, clamped to AMD-6 D12's ceiling. */
  grain?: number;
  /**
   * Let the bloom skirt carry alpha off the silhouette. The glow is how a bright object
   * belongs to this game's frame; clipping it to the source alpha reads as a cut-out.
   */
  glowSpill?: boolean;
}

// ---------------------------------------------------------------------------
// Sampling
// ---------------------------------------------------------------------------

interface Plane {
  w: number;
  h: number;
  data: Float32Array; // rgb, 3 per texel
}

function makePlane(w: number, h: number): Plane {
  return { w, h, data: new Float32Array(w * h * 3) };
}

const s3 = new Float32Array(3);

/** The scene target is nearest-filtered (see the note at the top of `crt.ts`). */
function nearest(p: Plane, u: number, v: number, out: Float32Array): void {
  let x = Math.floor(u * p.w);
  let y = Math.floor(v * p.h);
  x = x < 0 ? 0 : x >= p.w ? p.w - 1 : x;
  y = y < 0 ? 0 : y >= p.h ? p.h - 1 : y;
  const o = (y * p.w + x) * 3;
  out[0] = p.data[o];
  out[1] = p.data[o + 1];
  out[2] = p.data[o + 2];
}

/** Single-channel nearest fetch. Coverage is one float per texel, not three. */
function nearest1(data: Float32Array, w: number, h: number, u: number, v: number): number {
  let x = Math.floor(u * w);
  let y = Math.floor(v * h);
  x = x < 0 ? 0 : x >= w ? w - 1 : x;
  y = y < 0 ? 0 : y >= h ? h - 1 : y;
  return data[y * w + x];
}

/** Pyramid levels are `LinearFilter`; nearest here reintroduces the blockiness. */
function bilinear(p: Plane, u: number, v: number, out: Float32Array): void {
  const fx = u * p.w - 0.5;
  const fy = v * p.h - 0.5;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = fx - x0;
  const ty = fy - y0;
  const cx0 = x0 < 0 ? 0 : x0 >= p.w ? p.w - 1 : x0;
  const cy0 = y0 < 0 ? 0 : y0 >= p.h ? p.h - 1 : y0;
  const cx1 = x0 + 1 < 0 ? 0 : x0 + 1 >= p.w ? p.w - 1 : x0 + 1;
  const cy1 = y0 + 1 < 0 ? 0 : y0 + 1 >= p.h ? p.h - 1 : y0 + 1;
  const a = (cy0 * p.w + cx0) * 3;
  const b = (cy0 * p.w + cx1) * 3;
  const c = (cy1 * p.w + cx0) * 3;
  const d = (cy1 * p.w + cx1) * 3;
  for (let i = 0; i < 3; i++) {
    const top = p.data[a + i] + (p.data[b + i] - p.data[a + i]) * tx;
    const bot = p.data[c + i] + (p.data[d + i] - p.data[c + i]) * tx;
    out[i] = top + (bot - top) * ty;
  }
}

// ---------------------------------------------------------------------------
// Bloom — bloom.ts + Bloom.ts, transcribed
// ---------------------------------------------------------------------------

const TAP13_OFFSETS: readonly (readonly [number, number, number])[] = [
  [-2, -2, 0.03125],
  [0, -2, 0.0625],
  [2, -2, 0.03125],
  [-2, 0, 0.0625],
  [0, 0, 0.125],
  [2, 0, 0.0625],
  [-2, 2, 0.03125],
  [0, 2, 0.0625],
  [2, 2, 0.03125],
  [-1, -1, 0.125],
  [1, -1, 0.125],
  [-1, 1, 0.125],
  [1, 1, 0.125],
];

const TENT_OFFSETS: readonly (readonly [number, number, number])[] = [
  [-1, 1, 0.0625],
  [0, 1, 0.125],
  [1, 1, 0.0625],
  [-1, 0, 0.125],
  [0, 0, 0.25],
  [1, 0, 0.125],
  [-1, -1, 0.0625],
  [0, -1, 0.125],
  [1, -1, 0.0625],
];

/** Soft-knee bright pass, applied per tap so a lone hot pixel cannot be averaged away. */
function prefilter(out: Float32Array, threshold: number, knee: number): void {
  const r = Math.max(out[0], 0);
  const g = Math.max(out[1], 0);
  const b = Math.max(out[2], 0);
  const br = Math.max(r, Math.max(g, b));
  let rq = br - threshold + knee;
  rq = rq < 0 ? 0 : rq > 2 * knee ? 2 * knee : rq;
  rq = (rq * rq) / (4 * knee + 1e-4);
  const k = Math.max(rq, br - threshold) / Math.max(br, 1e-4);
  out[0] = r * k;
  out[1] = g * k;
  out[2] = b * k;
}

/**
 * The composite gain that produces `strength` independent of the bright area — the
 * telescoping-gain normalisation from `Bloom.normalisedGain`.
 */
function normalisedGain(strength: number, levels: number, weights: readonly number[]): number {
  let telescoped = 1;
  for (let i = levels - 1; i >= 1; i--) telescoped = 1 + (weights[i] ?? 1) * telescoped;
  return strength / Math.max(1, telescoped);
}

// ---------------------------------------------------------------------------
// The composed tube
// ---------------------------------------------------------------------------

interface Scratch {
  srcW: number;
  srcH: number;
  levels: Plane[];
  halA: Plane;
  halB: Plane;
}

let scratch: Scratch | null = null;

/**
 * Reused across every portrait of a given source size. Composing is not per-frame work,
 * but the death card recomposes on every run and a per-run pyramid allocation is free
 * to avoid.
 */
function getScratch(w: number, h: number): Scratch {
  if (scratch !== null && scratch.srcW === w && scratch.srcH === h) return scratch;
  const levels: Plane[] = [];
  for (let i = 0; i < TUBE.bloomWeights.length; i++) {
    levels.push(makePlane(Math.max(1, w >> (i + 1)), Math.max(1, h >> (i + 1))));
  }
  const hw = Math.max(1, w >> 3);
  const hh = Math.max(1, h >> 3);
  scratch = { srcW: w, srcH: h, levels, halA: makePlane(hw, hh), halB: makePlane(hw, hh) };
  return scratch;
}

/**
 * Runs the tube over a linear-light source and writes 8-bit sRGB RGBA to `out`.
 *
 * `srcRgb` is `w*h*3` linear floats, `srcA` is `w*h` coverage in 0..1, and `out` is
 * `(w*scale)*(h*scale)*4` bytes. Nothing here allocates after the first call at a given
 * source size.
 */
export function applyTube(
  srcRgb: Float32Array,
  srcA: Float32Array,
  w: number,
  h: number,
  out: Uint8ClampedArray,
  opts: TubeOptions
): void {
  const scale = Math.max(1, Math.round(opts.scale));
  const outW = w * scale;
  const outH = h * scale;

  const sc = getScratch(w, h);
  const src: Plane = { w, h, data: srcRgb };
  const levels = sc.levels;
  const nLevels = levels.length;

  // --- bright pass fused with the first downsample -------------------------
  const threshold = TUBE.bloomThreshold;
  const knee = Math.max(1e-3, TUBE.bloomKneeRatio * threshold);
  {
    const dst = levels[0];
    const tx = 1 / w;
    const ty = 1 / h;
    for (let y = 0; y < dst.h; y++) {
      for (let x = 0; x < dst.w; x++) {
        const u = (x + 0.5) / dst.w;
        const v = (y + 0.5) / dst.h;
        let ar = 0;
        let ag = 0;
        let ab = 0;
        for (const [ox, oy, wt] of TAP13_OFFSETS) {
          nearest(src, u + tx * ox, v + ty * oy, s3);
          prefilter(s3, threshold, knee);
          ar += s3[0] * wt;
          ag += s3[1] * wt;
          ab += s3[2] * wt;
        }
        const o = (y * dst.w + x) * 3;
        dst.data[o] = ar;
        dst.data[o + 1] = ag;
        dst.data[o + 2] = ab;
      }
    }
  }

  // --- halving chain ------------------------------------------------------
  for (let i = 1; i < nLevels; i++) {
    const s = levels[i - 1];
    const dst = levels[i];
    const tx = 1 / s.w;
    const ty = 1 / s.h;
    for (let y = 0; y < dst.h; y++) {
      for (let x = 0; x < dst.w; x++) {
        const u = (x + 0.5) / dst.w;
        const v = (y + 0.5) / dst.h;
        let ar = 0;
        let ag = 0;
        let ab = 0;
        for (const [ox, oy, wt] of TAP13_OFFSETS) {
          bilinear(s, u + tx * ox, v + ty * oy, s3);
          ar += s3[0] * wt;
          ag += s3[1] * wt;
          ab += s3[2] * wt;
        }
        const o = (y * dst.w + x) * 3;
        dst.data[o] = ar;
        dst.data[o + 1] = ag;
        dst.data[o + 2] = ab;
      }
    }
  }

  // --- halation, read off mip 2 BEFORE the upsample writes back into it ----
  {
    const source = levels[Math.min(2, nLevels - 1)];
    const a = sc.halA;
    const b = sc.halB;
    const tx = 1 / a.w;
    const ty = 1 / a.h;
    for (let y = 0; y < a.h; y++) {
      for (let x = 0; x < a.w; x++) {
        const u = (x + 0.5) / a.w;
        const v = (y + 0.5) / a.h;
        let ar = 0;
        let ag = 0;
        let ab = 0;
        for (const [ox, oy] of [
          [-1, -1],
          [1, -1],
          [-1, 1],
          [1, 1],
        ]) {
          bilinear(source, u + tx * ox, v + ty * oy, s3);
          ar += s3[0];
          ag += s3[1];
          ab += s3[2];
        }
        const o = (y * a.w + x) * 3;
        a.data[o] = ar * 0.25;
        a.data[o + 1] = ag * 0.25;
        a.data[o + 2] = ab * 0.25;
      }
    }
    blur9(a, b, 1, 0);
    blur9(b, a, 0, 1);
  }

  // --- tented accumulate back down to level 0 -----------------------------
  for (let i = nLevels - 1; i >= 1; i--) {
    const s = levels[i];
    const dst = levels[i - 1];
    const weight = TUBE.bloomWeights[i] ?? 1;
    const dx = (1 / s.w) * TUBE.bloomRadius;
    const dy = (1 / s.h) * TUBE.bloomRadius;
    for (let y = 0; y < dst.h; y++) {
      for (let x = 0; x < dst.w; x++) {
        const u = (x + 0.5) / dst.w;
        const v = (y + 0.5) / dst.h;
        let ar = 0;
        let ag = 0;
        let ab = 0;
        for (const [ox, oy, wt] of TENT_OFFSETS) {
          bilinear(s, u + dx * ox, v + dy * oy, s3);
          ar += s3[0] * wt;
          ag += s3[1] * wt;
          ab += s3[2] * wt;
        }
        const o = (y * dst.w + x) * 3;
        dst.data[o] += ar * weight;
        dst.data[o + 1] += ag * weight;
        dst.data[o + 2] += ab * weight;
      }
    }
  }

  // --- composite ----------------------------------------------------------
  const bloomIntensity = normalisedGain(TUBE.bloomStrength, nLevels, TUBE.bloomWeights);
  const maskFade = smoothstep(1.0, 2.5, scale);
  const scanline = Math.max(0, Math.min(SCANLINE_MAX, opts.scanline ?? SCANLINE_DEFAULT));
  const grain = Math.max(0, Math.min(GRAIN_MAX, opts.grain ?? TUBE.noise));
  const glowSpill = opts.glowSpill !== false;
  const bloom = levels[0];
  const hal = sc.halA;

  const soft = 1.5 / outH;
  const softX = 1.5 / outW;

  const bl = new Float32Array(3);
  const hl = new Float32Array(3);
  const cr = new Float32Array(3);
  const cb = new Float32Array(3);

  for (let py = 0; py < outH; py++) {
    for (let px = 0; px < outW; px++) {
      const u = (px + 0.5) / outW;
      const v = (py + 0.5) / outH;
      // `warp()` is the identity below the desktop framing (AMD-6 D12), which is where
      // every portrait lives.
      const wx = u;
      const wy = v;

      const edgeX = smoothstep(0, softX, wx) * smoothstep(0, softX, 1 - wx);
      const edgeY = smoothstep(0, soft, wy) * smoothstep(0, soft, 1 - wy);
      const inside = edgeX * edgeY;

      const cx = u * 2 - 1;
      const ca = (TUBE.chromaticAberration * cx) / outW;

      nearest(src, wx + ca, wy, cr);
      nearest(src, wx, wy, s3);
      nearest(src, wx - ca, wy, cb);
      let r = cr[0];
      let g = s3[1];
      let b = cb[2];

      bilinear(bloom, wx, wy, bl);
      r += bl[0] * bloomIntensity;
      g += bl[1] * bloomIntensity;
      b += bl[2] * bloomIntensity;

      bilinear(hal, wx, wy, hl);
      r += hl[0] * TUBE.halationTint[0] * TUBE.halationStrength;
      g += hl[1] * TUBE.halationTint[1] * TUBE.halationStrength;
      b += hl[2] * TUBE.halationTint[2] * TUBE.halationStrength;

      r *= TUBE.exposure;
      g *= TUBE.exposure;
      b *= TUBE.exposure;

      // gl_FragCoord: y counts up from the bottom of the frame.
      const fx = px + 0.5;
      const fy = outH - 0.5 - py;

      const phase = (Math.PI * 2 * fx) / TUBE.maskPitch;
      const gr = (0.5 + 0.5 * Math.cos(phase)) * 2;
      const gg = (0.5 + 0.5 * Math.cos(phase - 2.09439510239)) * 2;
      const gb = (0.5 + 0.5 * Math.cos(phase - 4.18879020479)) * 2;
      const ms = TUBE.maskStrength * maskFade;
      const maskR = 1 + (gr - 1) * ms;
      const maskG = 1 + (gg - 1) * ms;
      const maskB = 1 + (gb - 1) * ms;

      const row = wy * h;
      const band = Math.pow(0.5 - 0.5 * Math.cos(Math.PI * 2 * fract(row)), TUBE.scanlineSharpness);
      const scan = 1 + (1 - scanline + scanline * band - 1) * maskFade;
      r *= scan;
      g *= scan;
      b *= scan;

      const vigT = Math.min(1, Math.max(0, 16 * wx * wy * (1 - wx) * (1 - wy)));
      const vig = 1 - TUBE.vignette + TUBE.vignette * Math.pow(vigT, 0.12);
      r *= vig;
      g *= vig;
      b *= vig;

      const n = hash12(fx, fy) - 0.5;
      const mul = 1 + n * grain * 0.5;
      const add = n * grain * 0.5;
      r = r * mul + add;
      g = g * mul + add;
      b = b * mul + add;

      r *= inside;
      g *= inside;
      b *= inside;

      const shown = acesFilm(Math.max(r, 0), Math.max(g, 0), Math.max(b, 0));
      const sr = shown[0];
      const sg = shown[1];
      const sb = shown[2];

      // The grille is applied to the DISPLAYED value and renormalised to constant
      // luminance, so it redistributes energy between channels instead of dimming.
      let tr = sr * maskR;
      let tg = sg * maskG;
      let tb = sb * maskB;
      const lNorm = luma(sr, sg, sb) / Math.max(luma(tr, tg, tb), 1e-4);
      tr *= lNorm;
      tg *= lNorm;
      tb *= lNorm;

      const hi = Math.max(tr, Math.max(tg, tb));
      const sh = Math.max(sr, Math.max(sg, sb));
      if (hi > 1) {
        const k = (hi - 1) / Math.max(hi - sh, 1e-4);
        tr += (sr - tr) * k;
        tg += (sg - tg) * k;
        tb += (sb - tb) * k;
      }

      let a = nearest1(srcA, w, h, wx, wy);
      if (glowSpill) {
        const glow = luma(bl[0], bl[1], bl[2]) * bloomIntensity + luma(hl[0], hl[1], hl[2]) * TUBE.halationStrength;
        a = Math.max(a, Math.min(1, glow * 2.5));
      }
      a *= inside;

      const o = (py * outW + px) * 4;
      out[o] = linearToSrgb(Math.min(1, Math.max(0, tr))) * 255;
      out[o + 1] = linearToSrgb(Math.min(1, Math.max(0, tg))) * 255;
      out[o + 2] = linearToSrgb(Math.min(1, Math.max(0, tb))) * 255;
      out[o + 3] = a * 255;
    }
  }
}

/** Separable nine-tap gaussian, `HALATION_BLUR_FRAG`. */
function blur9(from: Plane, to: Plane, dirX: number, dirY: number): void {
  const W0 = 0.227027027;
  const W1 = 0.1945945946;
  const W2 = 0.1216216216;
  const W3 = 0.0540540541;
  const W4 = 0.0162162162;
  const sx = (1 / from.w) * dirX * TUBE.halationBlurRadius;
  const sy = (1 / from.h) * dirY * TUBE.halationBlurRadius;
  const weights = [W1, W2, W3, W4];
  for (let y = 0; y < to.h; y++) {
    for (let x = 0; x < to.w; x++) {
      const u = (x + 0.5) / to.w;
      const v = (y + 0.5) / to.h;
      bilinear(from, u, v, s3);
      let ar = s3[0] * W0;
      let ag = s3[1] * W0;
      let ab = s3[2] * W0;
      for (let i = 0; i < 4; i++) {
        const k = i + 1;
        bilinear(from, u + sx * k, v + sy * k, s3);
        ar += s3[0] * weights[i];
        ag += s3[1] * weights[i];
        ab += s3[2] * weights[i];
        bilinear(from, u - sx * k, v - sy * k, s3);
        ar += s3[0] * weights[i];
        ag += s3[1] * weights[i];
        ab += s3[2] * weights[i];
      }
      const o = (y * to.w + x) * 3;
      to.data[o] = ar;
      to.data[o + 1] = ag;
      to.data[o + 2] = ab;
    }
  }
}
